import React from 'react';

/**
 * COMPONENTS
 *
 * STANDARD COMPONENTS
 * */
import ErrorBoundary from './components/errorboundary/ErrorBoundary';
import Footer, { FooterProps } from './components/footer/Footer';
import Header, { HeaderProps } from './components/header/Header';
import Loading, { LoadingProps } from './components/loading/Loading';
import App, { AppProps } from './components/app/App';

type ComponentClassAsync<T> = Promise<{ default: React.ComponentClass<T> }>;

export class ComponentFactory {
  static get<T>(component: any): new () => React.Component<T> {
    return component;
  }

  static getAsync<T>(component: any): ComponentClassAsync<T> {
    return component;
  }

  static get AssetsBreakdownChartAsync() {
    return import(
      /* webpackChunkName: "AssetsBreakdownChart" */ './components/assetsbreakdownchart/AssetsBreakdownChart'
    );
  }

  static get AssetsGalleryAsync() {
    return import(/* webpackChunkName: "AssetsGallery" */ './components/assetsgallery/AssetsGallery');
  }

  static get App() {
    return ComponentFactory.get<AppProps>(App);
  }

  static get AuthAsync() {
    return import(/* webpackChunkName: "Auth" */ './pages/auth/Auth');
  }

  static get BeanToolTipAsync() {
    return import(/* webpackChunkName: "BeanToolTip" */ './components/beantooltip/BeanToolTip');
  }

  static get CardAsync() {
    return import(/* webpackChunkName: "Card" */ './components/card/Card');
  }

  static get ChartUtilsAsync() {
    return import(/* webpackChunkName: "ChartUtils" */ './components/chartutils/ChartUtils');
  }

  static get DistributionsChartAsync() {
    return import(/* webpackChunkName: "DistributionsChart" */ './components/distributionschart/DistributionsChart');
  }

  static get ErrorBoundary() {
    return ComponentFactory.get(ErrorBoundary);
  }

  static get ErrorPageAsync() {
    return import(/* webpackChunkName: "ErrorPage" */ './pages/errorpage/ErrorPage');
  }

  static get FinancialsAsync() {
    return import(/* webpackChunkName: "Financials" */ './components/financials/Financials');
  }

  static get Footer() {
    return ComponentFactory.get<FooterProps>(Footer);
  }

  static get Header() {
    return ComponentFactory.get<HeaderProps>(Header);
  }

  static get Loading() {
    return ComponentFactory.get<LoadingProps>(Loading);
  }

  static get NavPerShareChartAsync() {
    return import(/* webpackChunkName: "NavPerShareChart" */ './components/navpersharechart/NavPerShareChart');
  }

  static get OverviewAsync() {
    return import(/* webpackChunkName: "Overview" */ './pages/overview/Overview');
  }

  static get OverviewChartAsync() {
    return import(/* webpackChunkName: "OverviewChart" */ './components/overviewchart/OverviewChart');
  }

  static get OverviewMapAsync() {
    return import(/* webpackChunkName: "OverviewMap" */ './components/overviewmap/OverviewMap');
  }

  static get OverviewTileAsync() {
    return import(/* webpackChunkName: "OverviewTile" */ './components/overviewtile/OverviewTile');
  }

  static get ProductLinksAsync() {
    return import(/* webpackChunkName: "ProductLinks" */ './components/productlinks/ProductLinks');
  }

  static get PropertiesAsync() {
    return import(/* webpackChunkName: "Properties" */ './pages/properties/Properties');
  }

  static get PropertyDetailsAsync() {
    return import(/* webpackChunkName: "PropertyDetails" */ './components/propertydetails/PropertyDetails');
  }

  static get PropertyListAsync() {
    return import(/* webpackChunkName: "PropertyList" */ './components/propertylist/PropertyList');
  }

  static get PropertyMapAsync() {
    return import(/* webpackChunkName: "PropertyMap" */ './components/propertymap/PropertyMap');
  }

  static get PropertyNftAsync() {
    return import(/* webpackChunkName: "PropertyNft" */ './pages/propertynft/PropertyNft');
  }

  static get PropertyTokenCardAsync() {
    return import(/* webpackChunkName: "PropertyTokenCard" */ './components/propertytokencard/PropertyTokenCard');
  }

  static get TotalNavAndAssetValueChartAsync() {
    return import(
      /* webpackChunkName: "TotalNavAndAssetValueChart" */ './components/totalnavandassetvaluechart/TotalNavAndAssetValueChart'
    );
  }
}
